import type { Target } from 'src/models/TimerTarget';

import { CaretDownIcon } from '@phosphor-icons/react';
import { css, html } from 'react-strict-dom';
import { Color, Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { Model } from 'src/models';

import DropdownMenu from './DropdownMenu';
import DropdownMenuItem from './DropdownMenuItem';
import Text from './Text';

type Props = {
  /** Decides which categories the API will accept for this work. */
  target: Target;
  value: string | null;
  onChange: (category: string) => void;
  /** Held shut once a timer is running, which settles what it is logging as. */
  disabled?: boolean;
};

/**
 * Picks what a stretch of time was for.  Renders nothing against a target
 * which takes no category, so it can sit beside any timer unconditionally.
 */
export default function TimerCategorySelect({
  target,
  value,
  onChange,
  disabled = false,
}: Props) {
  const categories = Model.TimerTarget.getCategories(target);
  if (categories.length === 0) return null;

  if (disabled) return <Trigger value={value} />;

  return (
    <DropdownMenu trigger={<Trigger value={value} />} anchorSide="right">
      {(close) =>
        categories.map((category) => (
          <DropdownMenuItem
            key={category}
            showRadio={false}
            selected={category === value}
            onClick={() => {
              onChange(category);
              close();
            }}
          >
            {Model.WorkDoneCategory.format(category)}
          </DropdownMenuItem>
        ))
      }
    </DropdownMenu>
  );
}

function Trigger({ value }: { value: string | null }) {
  return (
    <html.div style={[Style.row, Style.alignCenter, styles.trigger]}>
      <Text
        typeface="caption.medium"
        color={value ? 'neutral100' : 'neutral40'}
      >
        {value ? Model.WorkDoneCategory.format(value) : 'Choose category'}
      </Text>
      <CaretDownIcon size={12} color={Color.neutral60} />
    </html.div>
  );
}

const styles = css.create({
  trigger: {
    height: 28,
    gap: 6,
    paddingLeft: 12,
    paddingRight: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    backgroundColor: color.neutral0,
    whiteSpace: 'nowrap',
  },
});
